import { formatNumberValue, type Language } from './formatUtils';

/** Binary units (same as File.size / OS tooling): 1 Go = 1024³ octets. */
export const BYTES_PER_MIB = 1024 * 1024;
export const BYTES_PER_GIB = 1024 * 1024 * 1024;

/** Plan `storage_mb` is stored as mebibytes (MiB). Convert to exact byte limit. */
export function storageMbToBytes(mb: number): number {
  return Math.round(mb * BYTES_PER_MIB);
}

const units = (language: Language) =>
  language === 'fr' ? { mb: 'Mo', gb: 'Go' } : { mb: 'MB', gb: 'GB' };

/** Human-readable size from bytes (Mo/Go or MB/GB). */
export function formatStorageFromBytes(bytes: number, language: Language): string {
  const { mb, gb } = units(language);
  if (bytes >= BYTES_PER_GIB) {
    const value = bytes / BYTES_PER_GIB;
    const decimals = Number.isInteger(value) || value >= 10 ? 0 : 1;
    return `${formatNumberValue(value, language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    })} ${gb}`;
  }
  const value = bytes / BYTES_PER_MIB;
  const decimals = Number.isInteger(value) ? 0 : value < 10 ? 2 : 1;
  return `${formatNumberValue(value, language, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  })} ${mb}`;
}

/**
 * Format current / limit with a shared unit (based on the limit when set).
 * Both values are in bytes — no float MB intermediate.
 */
export function formatStorageUsageFromBytes(
  currentBytes: number,
  limitBytes: number | null,
  language: Language,
  unlimitedLabel?: string
): string {
  const { mb, gb } = units(language);
  const useGb = (limitBytes ?? currentBytes) >= BYTES_PER_GIB;
  const divisor = useGb ? BYTES_PER_GIB : BYTES_PER_MIB;
  const unit = useGb ? gb : mb;

  const formatPart = (valueBytes: number): string => {
    const value = valueBytes / divisor;
    const decimals = useGb
      ? value === 0
        ? 0
        : value < 0.1
          ? 2
          : value < 10
            ? 1
            : 0
      : Number.isInteger(value)
        ? 0
        : value < 10
          ? 2
          : 1;
    return formatNumberValue(value, language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    });
  };

  const current = formatPart(currentBytes);
  if (limitBytes === null) {
    return unlimitedLabel
      ? `${current} ${unit} / ${unlimitedLabel}`
      : `${current} ${unit}`;
  }
  return `${current} ${unit} / ${formatPart(limitBytes)} ${unit}`;
}
